import React from 'react';
import { IconCheck, IconLock } from './Icons';
import './LockedFeaturePreview.css';

/**
 * Shown instead of a page the member's brand hasn't unlocked (Admin → Brands →
 * Features): the pitch on one side and, on the other, a drawn mock of the page
 * in a browser frame. Mocks are static, use made-up numbers and generic
 * strategy names (no brand names), and never load real data.
 */

const FEATURES = {
  overview: {
    title: 'Overview',
    pitch: 'See every strategy\'s growth, returns and risk on one page.',
    points: [
      'Growth chart and calculator for any starting balance',
      'Monthly returns, calendar and key insights',
      'Compare strategies against major benchmarks',
    ],
    mock: 'overview',
  },
  my_portfolio: {
    title: 'My Portfolio',
    pitch: 'Connect your broker accounts and watch your real results update automatically.',
    points: [
      'Connect NinjaTrader / Tradovate, Coinbase, Alpaca and Interactive Brokers',
      'Equity, growth, profit and drawdown charts with benchmark comparison',
      'Daily P&L calendar, trade history and holdings breakdown',
    ],
    mock: 'portfolio',
  },
  brand_results: {
    title: 'Results',
    pitch: 'Every strategy\'s full track record, trade by trade.',
    points: [
      'Calendar, chart, weekly and spreadsheet views',
      'Filter by weekday, roll week and high-impact news days',
      'Win rates, best and worst days, and drawdowns',
    ],
    mock: 'results',
  },
  portfolio_simulator: {
    title: 'Portfolio Simulator',
    pitch: 'Combine strategies and see how the portfolio would really have performed.',
    points: [
      'Rank strategies by profit factor, win rate, P&L or blowout risk',
      'Compare every weekday side by side',
      'Run risk analysis across all strategies at once',
    ],
    mock: 'simulator',
  },
  my_results: {
    title: 'My Results',
    pitch: 'Your own trading results, analyzed the same way as ours.',
    points: ['Upload or connect your trades', 'Equity curve and drawdown', 'Consistency checks'],
    mock: 'overview',
  },
  trade_watcher: {
    title: 'Trade Watcher',
    pitch: 'Watch your NinjaTrader accounts live and get alerted when a position has no stop.',
    points: [
      'Live connection status for every account',
      'Instant naked-position alerts by email',
      'Open and closed positions with P&L',
    ],
    mock: 'watcher',
  },
  blowout_risk: {
    title: 'Blowout Risk',
    pitch: 'Know how much capital you need before a drawdown ends your account.',
    points: ['A clear GO / NO GO for your account size', 'Risk score, worst loss and average loss', 'What to change to bring the risk down'],
    mock: 'blowout',
  },
  rule_risk: {
    title: '% Rule Risk',
    pitch: 'Check consistency-rule and trailing-drawdown risk before it costs you a funded account.',
    points: ['Maximum loss allowed under your rule', 'Largest safe profit day for the fastest payout', 'Probability of breaking the rule'],
    mock: 'rule',
  },
  hedging_risk: {
    title: 'Hedging Risk',
    pitch: 'Find strategy pairs that quietly cancel each other out.',
    points: ['Live trade overlap between strategies', 'Pairs that hedge each other', 'Daily P&L correlation at a glance'],
    mock: 'hedging',
  },
  prop_firm_planner: {
    title: 'Prop Firm Planner',
    pitch: 'See which strategy and size passed your prop firm’s evaluation most often.',
    points: ['Your firm’s verified rules, filled in', 'Every strategy tried at every size', 'Pass, blow-up and time-out rates'],
    mock: 'planner',
  },
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function PageHead({ title, sub }) {
  return (
    <div className="lfm-head">
      <b>{title}</b>
      {sub ? <small>{sub}</small> : null}
    </div>
  );
}

function Line({ points, alt }) {
  return <polyline className={`lfm-draw${alt ? ' is-alt' : ''}`} fill="none" points={points} />;
}

function Stats({ items, three }) {
  return (
    <div className={`lfm-stats${three ? ' lfm-stats-3' : ''}`}>
      {items.map(([k, v, t]) => (
        <div key={k}><small>{k}</small><strong className={t || ''}>{v}</strong></div>
      ))}
    </div>
  );
}

function MockOverview() {
  return (
    <>
      <PageHead title="Overview" sub="Strategy dashboard" />
      <div className="lfm-pills">
        {['Multi Account', 'Equity Portfolio 1×', 'Futures', 'Prime Pro'].map((p, i) => (
          <span key={p} className={i === 0 ? 'is-on' : ''}>{p}</span>
        ))}
      </div>
      <div className="lfm-panel lfm-grow">
        <div className="lfm-panel-top">
          <div><small>Growth of</small><strong>$100,000</strong><small>Strategy vs SPY</small></div>
          <div className="lfm-bench">
            <span className="is-on">S&amp;P 500 <em className="up">+0.73%</em></span>
            <span>Nasdaq-100 <em className="up">+1.00%</em></span>
            <span>Gold <em className="up">+1.11%</em></span>
          </div>
        </div>
        <svg className="lfm-chart" viewBox="0 0 400 120" preserveAspectRatio="none" aria-hidden="true">
          <Line points="0,110 30,102 60,98 90,90 120,94 150,80 180,84 210,66 240,58 270,46 300,40 330,26 360,14 380,22 400,18" />
          <Line alt points="0,114 50,110 100,108 150,104 200,98 250,94 300,88 350,82 400,76" />
        </svg>
      </div>
      <Stats items={[['Total return', '+250.1%', 'up'], ['Max drawdown', '-25.9%', 'down'], ['Sharpe', '1.43'], ['Win rate', '71.1%', 'up']]} />
    </>
  );
}

function MockSimulator() {
  const rows = [
    ['Momentum NQ', ['3.80', '2.70', '3.01', '4.50', '9.22'], '4.07'],
    ['Breakout ES', ['3.40', '2.78', '1.44', '5.69', '6.58'], '3.41'],
    ['Gold Swing', ['3.40', '2.78', '1.44', '5.69', '6.58'], '3.38'],
    ['Prime Pro', ['0.66', '6.84', '2.52', '0.00', '3.58'], '2.50'],
    ['Trend RTY', ['2.55', '1.21', '2.29', '1.79', '5.62'], '2.34'],
    ['Reversal CL', ['4.52', '1.25', '2.53', '1.12', '2.76'], '2.21'],
    ['Range ES', ['1.95', '2.10', '0.84', '2.67', '1.88'], '1.92'],
  ];
  return (
    <>
      <PageHead title="Portfolio Simulator" sub="Model combined strategies and projected outcomes" />
      <div className="lfm-controls">
        <span className="lfm-select">Profit Factor</span>
        <span className="lfm-seg"><i className="is-on">MNQ</i><i>NQ</i></span>
        <span className="lfm-select">All Time</span>
      </div>
      <div className="lfm-panel lfm-table">
        <div className="lfm-tr lfm-th lfm-sim">
          <span>#</span><span>Strategy</span>{DAYS.map((d) => <span key={d}>PF {d}</span>)}<span className="is-key">Overall</span>
        </div>
        {rows.map(([name, vals, overall], i) => (
          <div key={name} className="lfm-tr lfm-sim" style={{ animationDelay: `${i * 90}ms` }}>
            <span>{i + 1}</span><span className="lfm-link">{name}</span>
            {vals.map((v, j) => <span key={DAYS[j]} className={`lfm-num ${Number(v) < 1 ? 'down' : 'up'}`}>{v}</span>)}
            <span className="lfm-num up is-key">{overall}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function MockResults() {
  const rows = [
    ['Tuesday Special', 'FLAT', '$0', '', [0, 0, 0, 0, 0]],
    ['Thursday Special', 'LOSS', '-$1,965', 'down', [0, 0, 0, -1965, 0]],
    ['Prime Beef', 'WIN', '$3,320', 'up', [1360, 190, 205, 185, 1380]],
    ['Meat Popsicle', 'WIN', '$3,360', 'up', [-600, -615, 2295, -615, 2895]],
  ];
  return (
    <>
      <PageHead title="Results" sub="All strategy performance data, by strategy and weekday" />
      <Stats items={[['Winning strategies', '27', 'up'], ['Losing strategies', '29', 'down'], ['Largest win', '$10,590', 'up'], ['Largest loss', '-$9,215', 'down']]} />
      {rows.map(([name, tag, total, tone, days], i) => (
        <div key={name} className="lfm-panel lfm-strat" style={{ animationDelay: `${i * 110}ms` }}>
          <div className="lfm-strat-top">
            <b>{name}</b><em className={`lfm-tag ${tone}`}>{tag}</em><strong className={tone}>{total}</strong>
          </div>
          <div className="lfm-days">
            {days.map((v, j) => (
              <span key={DAYS[j]} className={v > 0 ? 'up' : v < 0 ? 'down' : ''}>
                {DAYS[j]}{v ? ` ${v > 0 ? '$' : '-$'}${Math.abs(v).toLocaleString()}` : ''}
              </span>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function MockBlowout() {
  return (
    <>
      <PageHead title="Blowout Risk Results" />
      <div className="lfm-panel lfm-verdict is-bad">
        <div className="lfm-verdict-top"><small>Account blowout risk</small><strong>NO GO</strong></div>
        <p>With 10 contracts the worst loss ($3,600) exceeds your max drawdown by $1,100. 5.6% of days breached it.</p>
      </div>
      <div className="lfm-cards">
        {[['Risk score', '54/100', ''], ['Highest loss', '$3,600', 'down'], ['Average loss', '$2,744', 'down'], ['Max profit', '$3,350', 'up'], ['Average profit', '$2,794', 'up'], ['Per-contract loss', '$360', 'down']].map(([k, v, t], i) => (
          <div key={k} className="lfm-panel" style={{ animationDelay: `${i * 80}ms` }}><small>{k}</small><strong className={t}>{v}</strong></div>
        ))}
      </div>
      <div className="lfm-panel lfm-fix">
        <b>What to fix</b>
        <p><strong>Reduce contracts:</strong> going from 10 to 6 contracts brings the risk under your drawdown.</p>
      </div>
    </>
  );
}

function MockRule() {
  return (
    <>
      <PageHead title="50% Rule Results" />
      <div className="lfm-panel lfm-verdict is-bad lfm-center">
        <em className="lfm-badge">REQUIRES ATTENTION</em>
        <b>50% Rule (Consistency / Windfall)</b>
      </div>
      <div className="lfm-cards lfm-cards-2">
        <div className="lfm-panel"><small>Maximum loss allowed</small><strong className="down lfm-big">$1,250</strong><small>50% of $2,500</small></div>
        <div className="lfm-panel"><small>Largest safe profit day</small><strong className="warn lfm-big">$1,300</strong><small>Payout at $2,600 is achievable</small></div>
        <div className="lfm-panel"><small>Worst historical loss day</small><strong className="down">$1,800</strong></div>
        <div className="lfm-panel"><small>Highest historical profit day</small><strong className="up">$1,675</strong></div>
      </div>
    </>
  );
}

function MockHedging() {
  const pairs = [['Momentum NQ', 'Reversal NQ', 82], ['Breakout ES', 'Fade ES', 64], ['Trend RTY', 'Range RTY', 41]];
  return (
    <>
      <PageHead title="Hedging Risk" sub="Strategies that take opposite positions at the same time" />
      <Stats three items={[['Futures strategies', '12'], ['Trades analyzed', '1,284'], ['Strategy pairs', '66']]} />
      <div className="lfm-panel lfm-table">
        <div className="lfm-tr lfm-th lfm-hedge"><span>Pair</span><span>Opposite days</span><span>Overlap</span></div>
        {pairs.map(([a, b, pct], i) => (
          <div key={a} className="lfm-tr lfm-hedge" style={{ animationDelay: `${i * 100}ms` }}>
            <span>{a} <i>vs</i> {b}</span>
            <span className="lfm-num down">{pct}%</span>
            <span className="lfm-bar"><i style={{ width: `${pct}%` }} /></span>
          </div>
        ))}
      </div>
    </>
  );
}

function MockPlanner() {
  const rows = [['Momentum NQ', '3 MNQ', 84], ['Breakout ES', '2 MNQ', 71], ['Trend RTY', '1 NQ', 52]];
  return (
    <>
      <PageHead title="Prop Firm Planner" sub="Topstep · Trading Combine · 50K" />
      <div className="lfm-panel lfm-table">
        <div className="lfm-tr lfm-th lfm-hedge"><span>Strategy · size</span><span>Passed</span><span /></div>
        {rows.map(([name, size, pct], i) => (
          <div key={name} className="lfm-tr lfm-hedge" style={{ animationDelay: `${i * 100}ms` }}>
            <span>{name} <i>·</i> {size}</span>
            <span className="lfm-num up">{pct}%</span>
            <span className="lfm-bar"><i style={{ width: `${pct}%` }} /></span>
          </div>
        ))}
      </div>
    </>
  );
}

function MockPortfolio() {
  return (
    <>
      <PageHead title="My Portfolio" sub="Your connected accounts" />
      <div className="lfm-panel lfm-table">
        <div className="lfm-tr lfm-th lfm-pf"><span>Name</span><span>Gain</span><span>Profit</span><span>Chart</span></div>
        {[['Futures Account', '+4.20%', '+$1,250'], ['Crypto', '+2.85%', '+$640']].map(([n, g, p], i) => (
          <div key={n} className="lfm-tr lfm-pf">
            <span>{n}</span><span className="lfm-num up">{g}</span><span className="lfm-num up">{p}</span>
            <svg viewBox="0 0 60 16" preserveAspectRatio="none" aria-hidden="true">
              <polyline fill="none" className="lfm-spark" points={i ? '0,12 10,11 20,13 30,8 40,9 50,5 60,4' : '0,14 10,12 20,13 30,9 40,7 50,6 60,2'} />
            </svg>
          </div>
        ))}
      </div>
      <div className="lfm-panel lfm-grow">
        <div className="lfm-tabs"><span className="is-on">Equity</span><span>Growth</span><span>Profit</span><span>Drawdown</span></div>
        <svg className="lfm-chart" viewBox="0 0 400 90" preserveAspectRatio="none" aria-hidden="true">
          <Line points="0,80 30,76 60,78 90,68 120,64 150,70 180,54 210,50 240,56 270,38 300,34 330,26 360,20 400,12" />
        </svg>
      </div>
      <div className="lfm-cal">
        {Array.from({ length: 14 }).map((_, i) => {
          const tone = [3, 8].includes(i) ? 'down' : [5, 12].includes(i) ? '' : 'up';
          // eslint-disable-next-line react/no-array-index-key
          return <i key={i} className={tone} style={{ animationDelay: `${i * 50}ms` }} />;
        })}
      </div>
    </>
  );
}

function MockWatcher() {
  const accounts = [['APEX-0003', 'ok', '+$412'], ['APEX-0007', 'ok', '+$128'], ['TOPSTEP-12', 'bad', '-$260'], ['APEX-0011', 'ok', '+$904'], ['MFF-221', 'warn', '$0'], ['APEX-0015', 'ok', '+$77']];
  return (
    <>
      <PageHead title="Trade Watcher" sub="Live NinjaTrader accounts" />
      <Stats items={[['Connected', '5', 'up'], ['Needs attention', '1', 'down'], ['Naked', '1', 'down'], ['Day P&L', '+$1,261', 'up']]} />
      <div className="lfm-board">
        {accounts.map(([name, tone, pnl]) => (
          <div key={name} className={`lfm-panel lfm-acct is-${tone}`}>
            <b>{name}</b>
            <small>{tone === 'bad' ? 'NAKED · no stop' : tone === 'warn' ? 'Offline' : 'Connected'}</small>
            <strong className={pnl.startsWith('-') ? 'down' : 'up'}>{pnl}</strong>
          </div>
        ))}
      </div>
    </>
  );
}

const MOCKS = {
  overview: MockOverview,
  simulator: MockSimulator,
  results: MockResults,
  blowout: MockBlowout,
  rule: MockRule,
  hedging: MockHedging,
  planner: MockPlanner,
  portfolio: MockPortfolio,
  watcher: MockWatcher,
};

export default function LockedFeaturePreview({ featureKey, brandName, onBack }) {
  const info = FEATURES[featureKey] || { title: 'This feature', pitch: '', points: [], mock: 'overview' };
  const Mock = MOCKS[info.mock] || MockOverview;

  return (
    <div className="lfp-page">
      <section className="lfp-copy" aria-label={`${info.title} is locked`}>
        <span className="lfp-lock-pill"><IconLock size={14} /> Locked</span>
        <h1>{info.title}</h1>
        {info.pitch ? <p className="lfp-pitch">{info.pitch}</p> : null}
        {info.points.length ? (
          <ul className="lfp-points">
            {info.points.map((p) => (
              <li key={p}><span className="lfp-check" aria-hidden="true"><IconCheck size={14} /></span>{p}</li>
            ))}
          </ul>
        ) : null}
        <div className="lfp-unlock">
          <strong>Want {info.title}?</strong>
          <span>
            It isn&apos;t included in {brandName ? <b>{brandName}</b> : 'your brand'}&apos;s plan yet.
            {' '}Speak to your brand owner to unlock it.
          </span>
        </div>
        {onBack ? <button type="button" className="lfp-btn" onClick={onBack}>Back</button> : null}
      </section>

      <figure className="lfp-media" aria-hidden="true">
        <div className="lfp-frame">
          <div className="lfp-frame-bar"><i /><i /><i /><span>{info.title}</span><em>Sample</em></div>
          <div className="lfp-frame-body">
            <div className="lfm">
              <Mock />
            </div>
          </div>
        </div>
      </figure>
    </div>
  );
}
